import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Module } from 'app/library/module/models/module.model';
import { ModuleState } from 'app/library/module/models/module.state';
import { ParticipantRegistrationMetric } from 'app/library/participant-registration-metric/models/participant-registration-metric.model';
import { SurveySectionApiService } from 'app/library/survey-section/services/survey-section.api-service';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import { map } from 'rxjs/operators';
import { ScormService } from '../../scorm/services/scorm.service';
import {
    AddScheduleToSection,
    CreateClasstimeFeedback,
    CreateClasstimeFeedbackFailure,
    CreateClasstimeFeedbackSuccess,
    CreateClasstimeQuiz,
    CreateClasstimeQuizFailure,
    CreateClasstimeQuizSuccess,
    CreateSection,
    CreateSectionFailure,
    CreateSectionSuccess,
    DeleteSection,
    DeleteSectionFailure,
    DeleteSectionSuccess,
    FetchModuleSections,
    FetchModuleSectionsFailure,
    FetchModuleSectionsSuccess,
    FetchSection,
    FetchSectionById,
    FetchSectionCourse,
    FetchSectionCourseFailure,
    FetchSectionCourseSuccess,
    FetchSectionFailure,
    FetchSectionImportStatus,
    FetchSectionImportStatusFailure,
    FetchSectionImportStatusSuccess,
    FetchSectionSuccess,
    GetLaunchCourseUrl,
    GetParticipantScormRegistrationMetricsFailure,
    GetParticipantScormRegistrationMetricsSuccess,
    ResetParticipantScormRegistrationMetrics,
    GetSectionMetrics,
    GetSectionMetricsFailure,
    GetSectionMetricsSuccess,
    RefreshFeedbackDataReport,
    RefreshFeedbackDataReportFailure,
    RefreshFeedbackDataReportSuccess,
    RefreshQuizDataReport,
    RefreshQuizDataReportFailure,
    RefreshQuizDataReportSuccess,
    RefreshSurveyDataReport,
    RefreshSurveyDataReportFailure,
    RefreshSurveyDataReportSuccess,
    RemoveScheduleFromSection,
    SetSection,
    SetSurveySection,
    SetSurveySectionExternal,
    SetSurveySectionFailure,
    SetSurveySectionSuccess,
    SubmitSurveyResponsesSuccess,
    ToggleSectionCompletion,
    ToggleSectionCompletionFailure,
    ToggleSectionCompletionSuccess,
    UnpublishSection,
    UnpublishSectionFailure,
    UnpublishSectionSuccess,
    UnsetSection,
    UnsetSectionMetrics,
    UpdateClasstimeQuiz,
    UpdateClasstimeQuizFailure,
    UpdateClasstimeQuizSuccess,
    UpdateScheduleInSection,
    UpdateSection,
    UpdateSectionFailure,
    UpdateSectionSuccess,
} from '../actions';
import { AddSections } from '../actions/add-entities.action';
import {
    FetchSectionByIdFailure,
    FetchSectionByIdSuccess,
    GetLaunchCourseUrlFailure,
    GetLaunchCourseUrlSuccess,
    GetParticipantScormRegistrationMetrics,
    SetSectionsMandatory,
    SetSectionsMandatoryFailure,
    SetSectionsMandatorySuccess,
    SetSurveyStatus,
    SubmitSurveyResponses,
    SubmitSurveyResponsesFailure,
} from '../actions/index';
import { SetSectionContentNodeId } from '../actions/set-section-content-node.action';
import { SectionType } from '../enums/section-type.enum';
import { SectionApiService } from '../services/section-api.service';
import { ELearningSection } from './eLearning-section.model';
import { FeedbackSection } from './feedback-section.model';
import { FilesSection } from './files-section.model';
import { IntegratedQuizSection } from './integrated-quiz-section.model';
import { OneFileSection } from './one-file-section.model';
import { QuizSection } from './quiz-section.model';
import { RecommendationsSection } from './recommendations-section.model';
import { SectionMetric } from './section-metrics.model';
import { Section } from './section.model';
import { SectionStateModel } from './section.state-model';
import { SurveySection } from './survey-section.model';
import { TextSection } from './text-section.model';
import { VideoSection } from './video-section.model';

@State<SectionStateModel>({
    name: 'section',
    defaults: new SectionStateModel(),
    children: [],
})
@Injectable()
export class SectionState extends EntityState<Section> {
    static sortProperties: string[] = ['sort'];

    @Selector()
    static getEntity(state: SectionStateModel, id: string = state.item): Section {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector([ModuleState.getEntity])
    static getModuleEntities(state: SectionStateModel, currentModule: Module): Section[] {
        if (!state.map) {
            return null;
        }

        if (!currentModule) {
            return null;
        }

        const toReturn = Array.from(state.map.values()).filter((section) => section.moduleId === currentModule.id);

        toReturn.sort(MaxBrainUtils.sortCompareFn(SectionState.sortProperties));

        return toReturn;
    }

    @Selector()
    static getParticipantScormRegistrationMetrics(state: SectionStateModel): ParticipantRegistrationMetric {
        if (!state.map) {
            return null;
        }

        return state.participantScormRegistrationMetrics;
    }

    @Selector()
    static getSectionMetrics(state: SectionStateModel): SectionMetric {
        if (!state.map) {
            return null;
        }

        return state.assessmentSectionMetrics;
    }

    constructor(private _sectionService: SectionApiService, private _scormService: ScormService, private _surveySectionApiService: SurveySectionApiService) {
        super(_sectionService, 30);

        this._propstoSearch = Section.props_to_search;
    }

    private _newSection(type: SectionType): Section {
        switch (type) {
            case SectionType.Files:
                return new FilesSection();
            case SectionType.Text:
                return new TextSection();
            case SectionType.Feedback:
                return new FeedbackSection();
            case SectionType.Quiz:
                return new QuizSection();
            case SectionType.IntegratedQuiz:
                return new IntegratedQuizSection();
            case SectionType.ELearning:
                return new ELearningSection();
            case SectionType.Survey:
                return new SurveySection();
            case SectionType.OneFile:
                return new OneFileSection();
            case SectionType.Video:
                return new VideoSection();
            case SectionType.Recommendations:
                return new RecommendationsSection();
        }

        return null;
    }

    @Action(AddSections)
    addEntities(ctx: StateContext<SectionStateModel>, action: AddSections): void {
        this._addEntities(ctx, action);
    }

    @Action(SetSection)
    setEntity(ctx: StateContext<SectionStateModel>, action: SetSection): void {
        this._setEntity(ctx, action, AddSections);
    }

    @Action(UnsetSection)
    unsetEntity(ctx: StateContext<SectionStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(UnsetSectionMetrics)
    unsetSectionMetrics(ctx: StateContext<SectionStateModel>): void {
        ctx.patchState({
            participantScormRegistrationMetrics: null,
            latestParticipantScormMetricsRequestSectionId: null,
        });
    }

    @Action(CreateSection)
    createEntity(ctx: StateContext<SectionStateModel>, action: CreateSection): void {
        this._createEntity(ctx, action, CreateSectionSuccess, CreateSectionFailure, AddSections, SetSection).then(() => {});
    }

    @Action(UpdateSection)
    updateEntity(ctx: StateContext<SectionStateModel>, action: UpdateSection): void {
        this._updateEntity(
            ctx,
            action,
            UpdateSectionSuccess,
            UpdateSectionFailure,
            AddSections,
            this.entityApiService.update(action.payload).pipe(
                map((section) =>
                    Object.assign<Section, Partial<Section>, Partial<Section>>(this._newSection(section.type), this.getSectionSettableProperties(section), {
                        contentNodeId: action.payload.contentNodeId || null,
                        userContentNodeId: action.payload.userContentNodeId || null,
                        schedules: action.payload.schedules,
                    })
                )
            )
        ).then(() => {});
    }

    @Action(DeleteSection)
    deleteEntity(ctx: StateContext<SectionStateModel>, { payload }: DeleteSection): void {
        this._sectionService.deleteSection(payload.id, payload.notify).subscribe(
            () => {
                this._removeEntitiesFromState(ctx, [payload.id]);
                ctx.dispatch(new DeleteSectionSuccess(payload.id));
            },
            (error) => ctx.dispatch(new DeleteSectionFailure(payload.id))
        );
    }

    @Action(FetchSection)
    fetchEntity(ctx: StateContext<SectionStateModel>, action: FetchSection): void {
        this._fetchEntity(ctx, action, FetchSectionSuccess, FetchSectionFailure, AddSections, SetSection).then(() => {});
    }

    @Action(FetchSectionSuccess)
    fetchEntitySuccess(ctx: StateContext<SectionStateModel>, action: FetchSectionSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchSectionFailure)
    fetchEntityFailure(ctx: StateContext<SectionStateModel>, action: FetchSectionFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    private getSectionSettableProperties(section: Section, removeNullValues: boolean = false): Partial<Section> {
        const toReturn = {};

        for (const propName in section) {
            if (propName !== 'type' && propName !== '_type' && propName !== 'constructor') {
                if (!removeNullValues) {
                    toReturn[propName] = section[propName];
                } else if (section[propName] !== null) {
                    toReturn[propName] = section[propName];
                }
            }
        }

        return toReturn;
    }

    private _setContentNodeId(ctx: StateContext<SectionStateModel>, { payload }: SetSectionContentNodeId, property: string): void {
        const state = ctx.getState();

        if (state.map && state.map.has(payload.sectionId)) {
            const itemFromState = state.map.get(payload.sectionId);
            const updatedItem = Object.assign<Section, Partial<Section>, Partial<Section>>(
                this._newSection((itemFromState as any)._type),
                this.getSectionSettableProperties(itemFromState),
                {
                    [property]: payload.contentNodeId,
                }
            );
            this._addEntities(ctx, { payload: [updatedItem] });
        }
    }

    @Action(FetchSectionById)
    fetchSectionById(ctx: StateContext<SectionStateModel>, { payload }: FetchSectionById): void {
        const state = ctx.getState();
        let itemFromState: Section;

        this._sectionService
            .fetchSectionById(payload)
            .pipe(
                map((section) => {
                    if (state.map && state.map.has(payload)) {
                        itemFromState = state.map.get(payload);

                        return Object.assign<Section, Partial<Section>, Partial<Section>>(this._newSection(section.type), this.getSectionSettableProperties(section), {
                            contentNodeId: itemFromState.contentNodeId || null,
                            userContentNodeId: itemFromState.userContentNodeId || null,
                            schedules: itemFromState.schedules,
                        });
                    }
                })
            )
            .subscribe(
                (entity) => {
                    ctx.dispatch([new AddSections([entity]), new SetSection(entity), new FetchSectionByIdSuccess(entity)]);
                },
                (error: HttpErrorResponse) => {
                    ctx.dispatch(new FetchSectionByIdFailure());
                }
            );
    }

    @Action(SetSectionContentNodeId)
    setModuleContentNodeId(ctx: StateContext<SectionStateModel>, action: SetSectionContentNodeId): void {
        this._setContentNodeId(ctx, action, 'contentNodeId');
    }

    @Action(ToggleSectionCompletion)
    toggleSectionCompletion(ctx: StateContext<SectionStateModel>, action: ToggleSectionCompletion): void {
        this._sectionService.toggleSectionCompletion(action.payload).subscribe(
            (payload) => {
                const state = ctx.getState();

                if (state.map) {
                    this._addEntities(ctx, {
                        payload: payload.map((payloadItem) => {
                            const itemFromState = state.map.get(payloadItem.id);
                            const updatedItem = Object.assign<Section, Partial<Section>, Partial<Section>>(
                                this._newSection(itemFromState.type),
                                this.getSectionSettableProperties(itemFromState),
                                this.getSectionSettableProperties(payloadItem, true)
                            );
                            return updatedItem;
                        }),
                    });
                }

                ctx.dispatch(new ToggleSectionCompletionSuccess(payload));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new ToggleSectionCompletionFailure(response.error));
            }
        );
    }

    @Action(UnpublishSection)
    unpublishSection(ctx: StateContext<SectionStateModel>, { payload }: UnpublishSection): void {
        this._sectionService.unpublishSection(payload).subscribe(
            () => {
                ctx.dispatch(new UnpublishSectionSuccess());
            },
            () => {
                ctx.dispatch(new UnpublishSectionFailure());
            }
        );
    }

    @Action(SubmitSurveyResponses)
    submitSurveyResponses(ctx: StateContext<SectionStateModel>, action: SubmitSurveyResponses): void {
        this._surveySectionApiService.submitResponses(action.payload).subscribe(
            () => {
                ctx.dispatch(new SubmitSurveyResponsesSuccess());
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new SubmitSurveyResponsesFailure());
            }
        );
    }

    @Action(RefreshSurveyDataReport)
    RefreshSurveyDataReport(ctx: StateContext<SectionStateModel>, action: RefreshSurveyDataReport): void {
        this._surveySectionApiService.refreshSurveySectionReportData(action.payload).subscribe(
            () => {
                ctx.dispatch(new RefreshSurveyDataReportSuccess());
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new RefreshSurveyDataReportFailure());
            }
        );
    }

    @Action(RefreshQuizDataReport)
    refreshQuizDataReport(ctx: StateContext<SectionStateModel>, action: RefreshSurveyDataReport): void {
        this._surveySectionApiService.refreshQuizSectionReportData(action.payload).subscribe(
            () => {
                ctx.dispatch(new RefreshQuizDataReportSuccess());
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new RefreshQuizDataReportFailure());
            }
        );
    }

    @Action(RefreshFeedbackDataReport)
    refreshFeedbackDataReport(ctx: StateContext<SectionStateModel>, action: RefreshSurveyDataReport): void {
        this._surveySectionApiService.refreshFeedbackSectionReportData(action.payload).subscribe(
            () => {
                ctx.dispatch(new RefreshFeedbackDataReportSuccess());
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new RefreshFeedbackDataReportFailure());
            }
        );
    }

    @Action(FetchModuleSections)
    fetchModuleSections({ getState, dispatch }: StateContext<SectionStateModel>, action: FetchModuleSections): void {
        this._sectionService
            .module(action.payload)
            .pipe(
                map((sections) => {
                    const state = getState();

                    return !state.map
                        ? sections
                        : sections.map((section) => {
                              const currentSection = state.map.get(section.id);

                              return Object.assign<Section, Partial<Section>, Partial<Section>>(this._newSection(section.type), this.getSectionSettableProperties(section), {
                                  contentNodeId: currentSection ? currentSection.contentNodeId || null : null,
                                  userContentNodeId: currentSection ? currentSection.userContentNodeId || null : null,
                              });
                          });
                })
            )
            .subscribe(
                (sections) => {
                    dispatch([new AddSections(sections), new FetchModuleSectionsSuccess(sections)]);
                },
                (e) => {
                    console.warn(e);
                    dispatch(new FetchModuleSectionsFailure());
                }
            );
    }

    @Action(CreateClasstimeQuiz)
    addClassroomQuizId(ctx: StateContext<SectionStateModel>, { payload }: CreateClasstimeQuiz): void {
        this._sectionService.createClasstimeQuiz(payload.sectionId, payload.options).subscribe(
            (section) => {
                const state = ctx.getState();

                if (state.map && state.map.has(payload.sectionId)) {
                    const itemFromState = state.map.get(payload.sectionId);
                    const updatedItem = Object.assign<Section, Partial<Section>, Partial<QuizSection>>(
                        this._newSection(itemFromState.type),
                        this.getSectionSettableProperties(itemFromState),
                        {
                            classtimeQuestionSetId: section.classtimeQuestionSetId,
                            isAssessmentLinkHidden: section.isAssessmentLinkHidden,
                            shuffleQuestions: section.shuffleQuestions,
                            shuffleQuestionOptions: section.shuffleQuestionOptions,
                            passingPercentage: section.passingPercentage,
                            markCompletedOnFail: section.markCompletedOnFail,
                            retryable: section.retryable,
                            hideFeedbackMode: section.hideFeedbackMode,
                            showScoreToParticipants: section.showScoreToParticipants,
                        }
                    );
                    this._addEntities(ctx, { payload: [updatedItem] });
                }

                ctx.dispatch(new CreateClasstimeQuizSuccess({ section }));
            },
            () => {
                ctx.dispatch(new CreateClasstimeQuizFailure());
            }
        );
    }

    @Action(UpdateClasstimeQuiz)
    updateClasstimeQuiz(ctx: StateContext<SectionStateModel>, { payload }: CreateClasstimeQuiz): void {
        this._sectionService.updateClasstimeQuiz(payload.sectionId, payload.options).subscribe(
            (section) => {
                const state = ctx.getState();

                if (state.map && state.map.has(payload.sectionId)) {
                    const itemFromState = state.map.get(payload.sectionId);
                    const updatedItem = Object.assign<Section, Partial<Section>, Partial<IntegratedQuizSection>>(
                        this._newSection(itemFromState.type),
                        this.getSectionSettableProperties(itemFromState),
                        {
                            dueDate: section.dueDate,
                            classtimeQuestionSetId: section.classtimeQuestionSetId,
                            isAssessmentLinkHidden: section.isAssessmentLinkHidden,
                            shuffleQuestions: section.shuffleQuestions,
                            shuffleQuestionOptions: section.shuffleQuestionOptions,
                            passingPercentage: section.passingPercentage,
                            markCompletedOnFail: section.markCompletedOnFail,
                            retryable: section.retryable,
                            hideFeedbackMode: section.hideFeedbackMode,
                            showScoreToParticipants: section.showScoreToParticipants,
                            allowRetryAfterCompletion: section.allowRetryAfterCompletion,
                            maxAttempts: section.maxAttempts,
                            showLastAttempt: section.showLastAttempt,
                        }
                    );
                    this._addEntities(ctx, { payload: [updatedItem] });
                }

                ctx.dispatch(new UpdateClasstimeQuizSuccess({ section }));
            },
            () => {
                ctx.dispatch(new UpdateClasstimeQuizFailure());
            }
        );
    }

    @Action(SetSurveySection)
    setSurveySection(ctx: StateContext<SectionStateModel>, { payload }: SetSurveySection): void {
        this._surveySectionApiService.setSurveySection(payload.sectionId, payload.surveyInfo, payload.descriptionVisibility, payload.description, payload.templateId).subscribe(
            (section) => {
                const state = ctx.getState();

                if (state.map && state.map.has(payload.sectionId)) {
                    const itemFromState = state.map.get(payload.sectionId);
                    const updatedItem = Object.assign<Section, Partial<Section>, Partial<SurveySection>>(
                        this._newSection(itemFromState.type),
                        this.getSectionSettableProperties(itemFromState),
                        {
                            survey: section.survey,
                        }
                    );
                    this._addEntities(ctx, { payload: [updatedItem] });
                }

                ctx.dispatch(new SetSurveySectionSuccess({ section }));
            },
            () => {
                ctx.dispatch(new SetSurveySectionFailure());
            }
        );
    }

    @Action(SetSurveyStatus)
    setSurveyStatus(ctx: StateContext<SectionStateModel>, { payload }: SetSurveyStatus): void {
        this._surveySectionApiService.setSurveyStatus(payload.sectionId, payload.status).subscribe(
            (section) => {
                const state = ctx.getState();

                if (state.map && state.map.has(payload.sectionId)) {
                    const itemFromState = state.map.get(payload.sectionId);
                    const updatedItem = Object.assign<Section, Partial<Section>, Partial<SurveySection>>(
                        this._newSection(itemFromState.type),
                        this.getSectionSettableProperties(itemFromState),
                        {
                            survey: section.survey,
                        }
                    );
                    this._addEntities(ctx, { payload: [updatedItem] });
                }

                ctx.dispatch(new SetSurveySectionSuccess({ section }));
            },
            () => {
                ctx.dispatch(new SetSurveySectionFailure());
            }
        );
    }

    @Action(SetSurveySectionExternal)
    setSurveySectionExternal(ctx: StateContext<SectionStateModel>, { payload }: SetSurveySectionExternal): void {
        this._surveySectionApiService.setSurveySectionExternal(payload.sectionId, payload.url, payload.description).subscribe(
            (section) => {
                const state = ctx.getState();

                if (state.map && state.map.has(payload.sectionId)) {
                    const itemFromState = state.map.get(payload.sectionId);
                    const updatedItem = Object.assign<Section, Partial<Section>, Partial<SurveySection>>(
                        this._newSection(itemFromState.type),
                        this.getSectionSettableProperties(itemFromState),
                        {
                            survey: section.survey,
                        }
                    );
                    this._addEntities(ctx, { payload: [updatedItem] });
                }

                ctx.dispatch(new SetSurveySectionSuccess({ section }));
            },
            () => {
                ctx.dispatch(new SetSurveySectionFailure());
            }
        );
    }

    @Action(CreateClasstimeFeedback)
    addClassroomFeedbackId(ctx: StateContext<SectionStateModel>, { payload }: CreateClasstimeFeedback): void {
        this._sectionService.createClasstimeFeedback(payload.sectionId, payload.options).subscribe(
            (section) => {
                const state = ctx.getState();

                if (state.map && state.map.has(payload.sectionId)) {
                    const itemFromState = state.map.get(payload.sectionId);
                    const updatedItem = Object.assign<Section, Partial<Section>, Partial<FeedbackSection>>(
                        this._newSection(itemFromState.type),
                        this.getSectionSettableProperties(itemFromState),
                        {
                            classtimeQuestionSetId: section.classtimeQuestionSetId,
                            isAssessmentLinkHidden: section.isAssessmentLinkHidden,
                            shuffleQuestions: section.shuffleQuestions,
                            shuffleQuestionOptions: section.shuffleQuestionOptions,
                            passingPercentage: section.passingPercentage,
                            markCompletedOnFail: section.markCompletedOnFail,
                            retryable: section.retryable,
                            hideFeedbackMode: section.hideFeedbackMode,
                            showScoreToParticipants: section.showScoreToParticipants,
                        }
                    );
                    this._addEntities(ctx, { payload: [updatedItem] });
                }

                ctx.dispatch(new CreateClasstimeFeedbackSuccess({ section }));
            },
            () => {
                ctx.dispatch(new CreateClasstimeFeedbackFailure());
            }
        );
    }

    @Action(GetLaunchCourseUrl)
    getLaunchCourseUrl({ dispatch }: StateContext<SectionStateModel>, action: GetLaunchCourseUrl): void {
        this._sectionService.getSectionLaunchCourseUrl(action.payload).subscribe(
            (entity) => {
                dispatch(new GetLaunchCourseUrlSuccess(entity.launchLink));
            },
            () => {
                dispatch(new GetLaunchCourseUrlFailure());
            }
        );
    }

    @Action(ResetParticipantScormRegistrationMetrics)
    resetParticipantScormRegistrationMetrics({ patchState }: StateContext<SectionStateModel>): void {
        patchState({
            participantScormRegistrationMetrics: null,
            latestParticipantScormMetricsRequestSectionId: null,
        });
    }

    @Action(GetParticipantScormRegistrationMetrics)
    getParticipantScormRegistrationMetrics({ dispatch, patchState, getState }: StateContext<SectionStateModel>, action: GetParticipantScormRegistrationMetrics): void {
        const requestedSectionId = action.payload;

        patchState({
            latestParticipantScormMetricsRequestSectionId: requestedSectionId,
        });

        this._sectionService.getSectionParticipantRegistrationData(requestedSectionId).subscribe(
            (entity) => {
                if (getState().latestParticipantScormMetricsRequestSectionId !== requestedSectionId) {
                    return;
                }

                patchState({
                    participantScormRegistrationMetrics: entity,
                });

                dispatch(new GetParticipantScormRegistrationMetricsSuccess(entity));
            },
            () => {
                if (getState().latestParticipantScormMetricsRequestSectionId !== requestedSectionId) {
                    return;
                }

                patchState({
                    participantScormRegistrationMetrics: null,
                });
                dispatch(new GetParticipantScormRegistrationMetricsFailure());
            }
        );
    }

    @Action(GetSectionMetrics)
    getAssessmentSectionMetrics({ dispatch, patchState }: StateContext<SectionStateModel>, action: GetSectionMetrics): void {
        this._sectionService.getAssessmentSectionMetrics(action.payload).subscribe(
            (entity) => {
                patchState({
                    assessmentSectionMetrics: entity,
                });

                dispatch(new GetSectionMetricsSuccess(entity));
            },
            () => {
                dispatch(new GetSectionMetricsFailure());
            }
        );
    }

    @Action(FetchSectionImportStatus)
    fetchImportStatus(ctx: StateContext<SectionStateModel>, { payload }: FetchSectionImportStatus): void {
        this._scormService.getImportStatus(payload.jobId).subscribe(
            (entity) => {
                const state = ctx.getState();

                if (state.map && state.map.has(payload.entityId)) {
                    const itemFromState = state.map.get(payload.entityId);
                    const updatedItem = Object.assign<ELearningSection, Partial<ELearningSection>, Partial<ELearningSection>>(
                        new ELearningSection(),
                        this.getSectionSettableProperties(itemFromState),
                        {
                            ['courseImportJobId']: payload.jobId,
                        }
                    );

                    this._addEntities(ctx, { payload: [updatedItem] });
                }

                switch (entity.status) {
                    case 'ERROR':
                        ctx.dispatch(new FetchSectionImportStatusFailure());
                        break;
                    case 'COMPLETE':
                        ctx.dispatch(new FetchSectionImportStatusSuccess(entity));
                        break;
                    default:
                        break;
                }
            },
            () => {
                ctx.dispatch(new FetchSectionImportStatusFailure());
            }
        );
    }

    @Action(FetchSectionCourse)
    fetchCourseStatus(ctx: StateContext<SectionStateModel>, { payload }: FetchSectionCourse): void {
        this._scormService.getCourseById(payload.courseId).subscribe(
            () => {
                ctx.dispatch(new FetchSectionCourseSuccess());
            },
            () => {
                ctx.dispatch(new FetchSectionCourseFailure());
            }
        );
    }

    @Action(AddScheduleToSection)
    addScheduleToSection(ctx: StateContext<SectionStateModel>, { payload }: AddScheduleToSection): void {
        const state = ctx.getState();
        let itemFromState = state.map.get(payload.entity.id);

        itemFromState.schedules.push(payload.scheduledJob);

        const schedules = itemFromState.schedules.map((schedule) => schedule);

        const updatedItem = Object.assign<Section, Partial<Section>, Partial<Section>>(this._newSection(payload.entity.type), this.getSectionSettableProperties(itemFromState), {
            schedules,
        });

        this._setEntities(ctx, { payload: [...Array.from(state.map.values()), updatedItem] });
    }

    @Action(UpdateScheduleInSection)
    updateSchedulelInSection(ctx: StateContext<SectionStateModel>, { payload }: UpdateScheduleInSection): void {
        const state = ctx.getState();
        let itemFromState = state.map.get(payload.entity.id);

        const schedules = itemFromState.schedules.map((schedule) => {
            if (schedule.id !== payload.scheduledJob.id) {
                schedule = payload.scheduledJob;
            }
            return schedule;
        });
        const updatedItem = Object.assign<Section, Partial<Section>, Partial<Section>>(this._newSection(payload.entity.type), this.getSectionSettableProperties(itemFromState), {
            schedules,
        });

        this._addEntities(ctx, { payload: [updatedItem] });
    }

    @Action(RemoveScheduleFromSection)
    removeScheduleFromSection(ctx: StateContext<SectionStateModel>, { payload }: RemoveScheduleFromSection): void {
        const state = ctx.getState();
        let itemFromState = state.map.get(payload.entity.id);

        const schedules = itemFromState.schedules.filter((schedule) => schedule.id !== payload.scheduledJobId);
        const updatedItem = Object.assign<Section, Partial<Section>, Partial<Section>>(this._newSection(payload.entity.type), this.getSectionSettableProperties(itemFromState), {
            schedules,
        });

        this._addEntities(ctx, { payload: [updatedItem] });
    }

    @Action(SetSectionsMandatory)
    setSectionMandatory({ dispatch }: StateContext<SectionStateModel>, { payload }: SetSectionsMandatory): void {
        this._sectionService.setSectionsMandatory(payload.sections, payload.isMandatory).subscribe(
            (entities) => {
                let updatedSections: Section[] = [];

                entities.forEach((entity) => {
                    payload.sections.forEach((section) => {
                        if (entity.id === section.id) {
                            updatedSections.push(
                                Object.assign<Section, Partial<Section>, Partial<Section>>(this._newSection(entity.type), this.getSectionSettableProperties(entity), {
                                    contentNodeId: section.contentNodeId || null,
                                    userContentNodeId: section.userContentNodeId || null,
                                    schedules: section.schedules,
                                })
                            );
                        }
                    });
                });

                dispatch([new AddSections(updatedSections), new SetSectionsMandatorySuccess(entities)]);
            },
            () => {
                dispatch(new SetSectionsMandatoryFailure());
            }
        );
    }
}
